/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import DateField from "@components/FormsV2/DateField/DateField";
import ControlledDropDownV2 from "@components/Forms/ControlledDropDownV2";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useConsumptionReport } from "./api";
import CreditsBarChart from "./CreditsBarChart";
import { defaultStartDate } from "./helpers";
import { BUCKET_SIZES, BucketSize } from "./types";
import { useStyles } from "./styles";

const StatCard: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => {
  const styles = useStyles();
  return (
    <Card body css={styles.summaryCard}>
      <div css={styles.statLabel}>{label}</div>
      <div css={styles.statValue}>{value}</div>
    </Card>
  );
};

const AccountCredits: React.FC = () => {
  const styles = useStyles();
  const [startDate, setStartDate] = useState<string>(defaultStartDate());
  const [bucketSize, setBucketSize] = useState<BucketSize>("month");
  const { runReport, report, isLoading, isUninitialized, error } =
    useConsumptionReport();
  const showSpinner = isLoading || isUninitialized;

  useEffect(() => {
    runReport({ startDate, bucketSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, bucketSize]);

  return (
    <div>
      <Row css={styles.summaryRow}>
        <Col md={3} css={styles.summaryCol}>
          <StatCard label="Starting Balance" value={report.startingBalance} />
        </Col>
        <Col md={3} css={styles.summaryCol}>
          <StatCard label="Current Balance" value={report.currentBalance} />
        </Col>
        <Col md={3} css={styles.summaryCol}>
          <StatCard label="Total Consumed" value={report.totalConsumed} />
        </Col>
        <Col md={3} css={styles.summaryCol}>
          <Card body css={styles.inputsCard}>
            <div css={styles.inputField}>
              <DateField
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div css={styles.inputField}>
              <ControlledDropDownV2
                label="Bucket Size"
                id="account-credits-bucket-size"
                value={bucketSize}
                onChange={(e) => setBucketSize(e.target.value as BucketSize)}
                required={false}
                readOnly={false}
                disabled={false}
                options={BUCKET_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size.charAt(0).toUpperCase() + size.slice(1)}
                  </option>
                ))}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Card css={styles.chartCard}>
        <Card.Header css={styles.chartCardHeader}>Account Credits</Card.Header>
        <Card.Body>
          {showSpinner && <ScopeStackSpinner />}

          {!showSpinner && Boolean(error) && (
            <div css={styles.errorState}>
              Could not load consumption report.
            </div>
          )}

          {!showSpinner && !error && report.buckets.length === 0 && (
            <div css={styles.emptyState}>No consumption in this period.</div>
          )}

          {!showSpinner && !error && report.buckets.length > 0 && (
            <div css={styles.chartContainer}>
              <CreditsBarChart
                buckets={report.buckets}
                bucketSize={bucketSize}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AccountCredits;
